import React from "react";
import Page from "../components/page";
import { graphql, Script } from "gatsby";
import {
  Body1,
  Heading1,
} from "@vorne/gatsby-theme-marketing/components/typography/typography";
import Grid from "@vorne/gatsby-theme-marketing/components/layout/grid/grid";
import Card, {
  CardTitle,
} from "@vorne/gatsby-theme-marketing/components/card/card";
import Image from "@vorne/gatsby-theme-marketing/components/image/image";
import Link from "@vorne/gatsby-theme-marketing/components/link/link";
import CallToActionButton from "@vorne/gatsby-theme-marketing/components/button/calltoaction/callToAction";
import Center from "@vorne/gatsby-theme-marketing/components/layout/center/center";
import Routes from "../routes";
import PageTitle from "@vorne/gatsby-theme-marketing/components/pagetitle/pageTitle";
import Section from "@vorne/gatsby-theme-marketing/components/section/section";
import OEEHomePageHero from "@vorne/gatsby-theme-marketing/components/hero/oee-homepage-hero";
import { Seo } from "@vorne/gatsby-theme-marketing/components/seo/v2";
import { OEEBanner } from "@vorne/gatsby-theme-marketing/components/banners";

const HomePage = ({ data }) => {
  return (
    <Page excludeWhatComesNext>
      <OEEHomePageHero />
      <Section>
        <PageTitle>Overall Equipment Effectiveness</PageTitle>
        <Heading1>What Is OEE?</Heading1>
        <Body1>
          OEE (Overall Equipment Effectiveness) is the gold standard for
          measuring manufacturing productivity. Simply put – it identifies the
          percentage of manufacturing time that is truly productive. An OEE
          score of 100% means you are manufacturing only Good Parts, as fast as
          possible, with no Stop Time. In the language of OEE that means 100%
          Quality (only Good Parts), 100% Performance (as fast as possible), and
          100% Availability (no Stop Time).
        </Body1>
        <Body1>
          <Link url={Routes.OEE.CalculatingOEE}>Measuring OEE</Link> is a
          manufacturing best practice. By measuring OEE and the{" "}
          <Link url={Routes.OEE.SixBigLosses}>underlying losses</Link>, you will
          gain important insights on how to systematically{" "}
          <Link url={Routes.OEE.ImproveOEE}>
            improve your manufacturing process
          </Link>
          . OEE is the single best metric for identifying losses, benchmarking
          progress, and improving the productivity of manufacturing equipment
          (i.e., eliminating waste).
        </Body1>
        <Body1>
          This website is devoted to mastering the art and science of OEE.
          Understand it. Measure it. Improve it.
        </Body1>
        <Body1>
          <Link url={Routes.OEE.Factors}>Start here</Link> on your path to
          mastering OEE.
        </Body1>
        <OEEBanner />
        <Grid $desktop="1 1 1 1">
          <Card>
            <Image src={data.availabilityIcon} alt="Availability Icon" />
            <CardTitle>Availability</CardTitle>
            <Body1>
              Availability takes into account Unplanned and Planned Stops. An
              Availability score of 100% means the process is always running
              during Planned Production Time.
            </Body1>
          </Card>
          <Card>
            <Image src={data.performanceIcon} alt="Performance Icon" />
            <CardTitle>Performance</CardTitle>
            <Body1>
              Performance takes into account Slow Cycles and Small Stops. A
              Performance score of 100% means when the process is running it is
              running as fast as possible.
            </Body1>
          </Card>
          <Card>
            <Image src={data.qualityIcon} alt="Quality Icon" />
            <CardTitle>Quality</CardTitle>
            <Body1>
              Quality takes into account Defects (including parts that need
              Rework). A Quality score of 100% means there are no Defects (only
              Good Parts are being produced).
            </Body1>
          </Card>
          <Card>
            <Image src={data.OEEIcon} alt="OEE Icon" />
            <CardTitle>Overall Equipment Effectiveness</CardTitle>
            <Body1>
              OEE takes into account all losses. An OEE score of 100% means you
              are manufacturing only Good Parts, as fast as possible, with no
              Stop Time.
            </Body1>
          </Card>
        </Grid>
        <Center $horizontally>
          <CallToActionButton url={Routes.Vorne.OEESoftware}>
            Get Started With OEE Software
          </CallToActionButton>
        </Center>
        <Heading1>Popular OEE Topics</Heading1>
        <Grid $desktop="1 1 1">
          <Card url={Routes.OEE.Factors}>
            <CardTitle>OEE Factors</CardTitle>
            <Body1>
              Understand the three underlying factors of OEE: Availability,
              Performance, and Quality.
            </Body1>
            <Image
              src={data.OEEBreakdownImage}
              alt="Chart of OEE three underlying factors: Availability, Performance, and Quality"
            />
          </Card>
          <Card url={Routes.OEE.ImplementingOEE}>
            <CardTitle>Implement OEE</CardTitle>
            <Body1>
              A simple step-by-step guide to measuring OEE for the first time.
            </Body1>
            <Image
              src={data.implementOEEImage}
              alt="Preview of step by step guides to measuring OEE with downloadable forms"
            />
          </Card>
          <Card url={Routes.OEE.WorldClassOEE}>
            <CardTitle>World-Class OEE</CardTitle>
            <Body1>
              Learn about the “mythical” world-class OEE and how to set an
              effective OEE target.
            </Body1>
            <Image
              src={data.worldClassOEEImage}
              alt="Chart of 85% OEE based on having 90% Availability, 95% Performance, and 99% Quality"
            />
          </Card>
          <Card url={Routes.OEE.CalculatingOEE}>
            <CardTitle>Calculate OEE</CardTitle>
            <Body1>
              Everything you need to calculate OEE for your manufacturing
              process.
            </Body1>
            <Image
              src={data.calculatingOEEImage}
              alt="Equation for calculating Overall Equipment Effectiveness by multiplying Availability, Performance, and Quality"
            />
          </Card>
          <Card url={Routes.OEE.SixBigLosses}>
            <CardTitle>Six Big Losses</CardTitle>
            <Body1>
              A foundation for improvement that leverages and expands upon the
              three OEE factors.
            </Body1>
            <Image
              src={data.OEESixBigLossesImage}
              alt="Chart of OEE Loss factors (Availability, Performance, and Quality) relative to the Six Big Losses"
            />
          </Card>
          <Card url={Routes.OEE.ImproveOEE}>
            <CardTitle>Improve OEE</CardTitle>
            <Body1>
              A simple and powerful methodology for improving OEE, with
              step-by-step example.
            </Body1>
            <Image
              src={data.improveOEEImage}
              alt="Process diagram for IDA (Information, Decision, Action) for improving productivity"
            />
          </Card>
        </Grid>
        <Heading1>Additional OEE Resources</Heading1>
        <Body1>
          OEE is presented by Vorne. We are experts in the field of improving
          manufacturing productivity and our flagship product is the{" "}
          <Link url={Routes.VorneWebsiteRoute}>XL Productivity Appliance</Link>
          ™. Our other websites include:
        </Body1>
        <Grid $desktop="1 1">
          <Card url={Routes.LeanProductionWebsiteRoute}>
            <Image
              src={data.leanProductionThumb}
              alt="Icon for Lean Production From Vorne"
            />
            <CardTitle>Lean Production</CardTitle>
            <Body1>
              Traditional lean manufacturing principles presented in an easy to
              understand format.
            </Body1>
            <Center $horizontally>
              <CallToActionButton url={Routes.LeanProductionWebsiteRoute}>
                Explore Lean Production Resources
              </CallToActionButton>
            </Center>
          </Card>
          <Card url={Routes.VorneWebsiteRoute}>
            <Image
              src={data.vorneThumb}
              alt="Icon for Visit Vorne Industries"
            />
            <CardTitle>Vorne</CardTitle>
            <Body1>
              Home of our flagship product – the XL Productivity Appliance™.
            </Body1>
            <Center $horizontally>
              <CallToActionButton url={Routes.VorneWebsiteRoute}>
                Learn About Vorne XL
              </CallToActionButton>
            </Center>
          </Card>
        </Grid>
      </Section>
      <Script src="https://fast.wistia.com/embed/medias/sd70gqidus.jsonp" />
      <Script src="https://fast.wistia.com/assets/external/E-v1.js" />
      <Script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: `{
                      "@context": "https://schema.org",
                      "@type": "Brand",
                      "@id": "#oeebrand",
                      "name": "OEE",
                      "url": "https://www.oee.com/",
                      "description": "The original and best resource for learning about Overall Equipment Effectiveness.",
                      "logo": {
                        "@type": "ImageObject",
                        "url": "https://www.oee.com/static/3de9aa6ed17354ddcad21a149c0afd3e/6689f/oee_logo.webp",
                        "width": "150",
                        "height": "84"
                      }
                    }`,
        }}
      />
    </Page>
  );
};

export default HomePage;

export function Head() {
  return (
    <Seo
      title="What Is OEE (Overall Equipment Effectiveness)?"
      description="Maximize manufacturing productivity by understanding the three OEE factors: Availability, Performance, and Quality."
    />
  );
}

export const query = graphql`
  query {
    availabilityIcon: file(relativePath: { eq: "availability-icon.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
    performanceIcon: file(relativePath: { eq: "performance-icon.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
    qualityIcon: file(relativePath: { eq: "quality-icon.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
    OEEIcon: file(relativePath: { eq: "oee-icon.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
    OEEBreakdownImage: file(relativePath: { eq: "oee-breakdown-image.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
    implementOEEImage: file(relativePath: { eq: "implement-oee-image.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
    worldClassOEEImage: file(
      relativePath: { eq: "world-class-oee-image.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
    calculatingOEEImage: file(
      relativePath: { eq: "calculating-oee-image.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
    OEESixBigLossesImage: file(
      relativePath: { eq: "oee-six-big-losses-image.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
    improveOEEImage: file(relativePath: { eq: "improve-oee-image.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
    leanProductionThumb: file(
      relativePath: { eq: "lean-production-thumb.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
    vorneThumb: file(relativePath: { eq: "vorne-thumb.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
  }
`;
